import { Button, ButtonProps, ExtendableButton } from "@outschool/backpack";
import {
  type TrackingProperties,
  useImpressionTracking,
} from "@outschool/ui-legacy-component-library";
import React from "react";

export type {
  ButtonColor as TrackedButtonColor,
  ButtonSize as TrackedButtonSize,
  ButtonVariant as TrackedButtonVariant,
} from "@outschool/backpack";

type TrackingProps = {
  trackingName?: string;
  /**
   * Has no effect on tracking and is forwarded to Button; use trackingName for
   * touch tracking.
   */
  trackingLabel?: string;
  /** Forwarded to the rendered element as a DOM attribute. */
  label?: string;
  trackingViewEventName?: string;
  trackingTouchEventName?: string;
  trackingUniqueId?: string;
  trackingSharedProperties?: TrackingProperties;
  immediateAction?(e: MouseEvent): void;
  trackViews?: boolean;
};

export type TrackedButtonProps<C extends React.ElementType = "button"> =
  ButtonProps<C> & TrackingProps;

/**
 * A tracked implementation of Backpack Button
 */
const _TrackedButton = <C extends React.ElementType = "button">(
  {
    trackingName,
    trackingViewEventName,
    trackingTouchEventName,
    trackingUniqueId,
    trackingSharedProperties,
    immediateAction,
    trackViews,
    onClick,
    ...props
  }: TrackedButtonProps<C> & { component?: C },
  ref: React.Ref<HTMLElement | null>,
) => {
  const impressionNodeRef = React.useRef<HTMLElement | null>(null);
  React.useImperativeHandle(ref, () => impressionNodeRef.current);

  const { trackTouch } = useImpressionTracking({
    node: impressionNodeRef.current,
    trackingLabel: trackingName!,
    uniqueId: trackingUniqueId!,
    trackingEventName: trackingTouchEventName,
    impressionEventName: trackingViewEventName,
    sharedProperties: trackingSharedProperties,
    trackViews,
  });

  return (
    <Button
      ref={impressionNodeRef as any}
      onClick={(e: any) => {
        if (trackingName) {
          trackTouch();
        }
        immediateAction?.(e);
        onClick?.(e);
      }}
      {...props}
    />
  );
};
_TrackedButton.displayName = "TrackedButton";

export type TrackedButtonComponent = ExtendableButton<TrackingProps>;

export const TrackedButton = React.forwardRef(
  _TrackedButton,
) as ExtendableButton<TrackingProps>;
